import React, { useState, useEffect } from 'react';
import { useUploadContext } from 'services/context/uploadContext/uploadContext';
import {
  UPLOAD_STATUSES,
  getUploadStatusesReadable
} from 'constants/uploadStatuses';
import { getEveryUpload } from 'utils/indexedDBService';

import LoadingIndicator from 'components/global/UploadNotificationPanel/LoadingIndicator';
import { StyledButton } from 'components/global/Buttons/styles/StyledComponents';
import {
  PanelContainer,
  PanelHeader,
  CloseButton,
  UploadContainer,
  UploadItemContainer,
  UploadItemHeader,
  FileInfoContainer,
  UploadStatusContainer,
  ErrorText,
  StatusText,
  IconContainer,
  ShowUploadsButton
} from 'components/global/UploadNotificationPanel/styles';
import { FILE_RECOVERY_SLUG } from 'utils/constants/constants';
import { useTranslation } from 'react-i18next';
export const UploadNotificationPanel = ({ urlUuid, urlSlug }) => {
  const { t } = useTranslation();
  const uploadStatusesReadable = getUploadStatusesReadable();
  const {
    state: { pendingUploads, idbUpdateTimestamp }
  } = useUploadContext();

  const [isOpen, setIsOpen] = useState(false);

  const [idbUploads, setIdbUploads] = useState([]);
  const [hasUploads, setHasUploads] = useState(false);
  const [hasErrors, setHasErrors] = useState(false);

  const isDebugRoute = urlSlug === FILE_RECOVERY_SLUG;

  useEffect(() => {
    // Here we get all the indexeddb uploads
    // Related to this attempt
    // So that if they come back to the page they can download it again,
    // And if they go to the root of the page
    // they can download any failed or pending files
    const fetchUploads = async () => {
      // If the urlUuid is not present we don't want to fetch the uploads
      // unless they are at the file recovery slug route, which should return every
      // file in the indexeddb
      if (!urlUuid && !isDebugRoute) return;
      const tempU = await getEveryUpload(urlUuid);
      setIdbUploads(tempU);
      setHasUploads(tempU?.length > 0);
      setHasErrors(
        tempU?.some(upload => upload.status === UPLOAD_STATUSES.ERROR)
      );
    };

    fetchUploads();
  }, [pendingUploads, urlUuid, urlSlug, isDebugRoute, idbUpdateTimestamp]);

  const togglePanel = () => {
    setIsOpen(!isOpen);
  };

  // This function allows the user to download the file
  // from the idb record
  const downloadUpload = (id, blob) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const extension = blob.name.substring(blob.name.lastIndexOf('.') + 1);
    a.download = `${id}.${extension}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <IconContainer>
        {hasUploads && (
          <ShowUploadsButton onClick={togglePanel} hasErrors={hasErrors}>
            {t('uploadFileQuestion.showUploads')}
          </ShowUploadsButton>
        )}
      </IconContainer>
      <PanelContainer isOpen={isOpen}>
        <PanelHeader>
          <h4>{t('uploadPanel.title')}</h4>
          <CloseButton onClick={() => setIsOpen(false)}>x</CloseButton>
        </PanelHeader>
        <UploadContainer>
          {idbUploads?.map(upload => {
            const {
              compositeId,
              status,
              error,
              blob,
              assessmentSlug,
              questionNumber
            } = upload;
            // remove the slotId from the compositeId
            const attemptUUID = compositeId
              .split('-')
              .slice(0, -1)
              .join('-');
            return (
              <UploadItemContainer key={compositeId}>
                <UploadItemHeader></UploadItemHeader>
                {isDebugRoute && (
                  <>
                    <FileInfoContainer>
                      <p>{t('uploadPanel.assessmentSlug')} </p>
                      <p>{assessmentSlug}</p>
                    </FileInfoContainer>
                    <FileInfoContainer>
                      <p>{t('uploadPanel.attemptUUID')} </p>
                      <p>{attemptUUID}</p>
                    </FileInfoContainer>
                  </>
                )}
                <FileInfoContainer>
                  <p>{t('uploadPanel.questionNumber')} </p>
                  <p>{questionNumber}</p>
                </FileInfoContainer>
                <FileInfoContainer>
                  <p>{t('uploadPanel.fileName')} </p>
                  <p>{blob && blob.name}</p>
                </FileInfoContainer>

                {/* If status is UPLOADING we show a simple loading indicator */}
                {status === UPLOAD_STATUSES.UPLOADING && <LoadingIndicator />}
                {status !== UPLOAD_STATUSES.ERROR && (
                  <UploadStatusContainer>
                    {uploadStatusesReadable[status]}
                  </UploadStatusContainer>
                )}
                {error && (
                  <>
                    <ErrorText>
                      {t('uploadPanel.errorLabel')} {error}
                    </ErrorText>
                    <StatusText>{t('uploadPanel.downloadMessage')}</StatusText>
                  </>
                )}
                <StyledButton
                  style={{
                    width: '100%'
                  }}
                  onClick={() => downloadUpload(compositeId, blob)}
                >
                  {t('uploadPanel.download')}
                </StyledButton>
              </UploadItemContainer>
            );
          })}
        </UploadContainer>
      </PanelContainer>
    </>
  );
};
